import React, { type FC, type ReactNode } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

/**
 * Canonical layout for an admin page in Confluence. Every admin-shell
 * page renders inside a centred, max-1024px column with consistent padding.
 */
const styles = cssMap({
	outer: {
		width: '100%',
		maxWidth: '100%',
		display: 'flex',
		justifyContent: 'center',
		overflowX: 'hidden',
		overflowY: 'hidden',
	},
	inner: {
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.500'),
		width: '100%',
		minWidth: '504px',
		maxWidth: '1024px',
		display: 'flex',
		flexDirection: 'column',
	},
});

// Matches Mission Control's ContentContainer for consistency.
const innerInlineStyle: React.CSSProperties = {
	paddingInlineStart: '112px', // Forced px sizing to match Mission Control's ContentContainer padding
	paddingInlineEnd: '112px',
	containerType: 'inline-size',
	containerName: 'AdminPage',
	boxSizing: 'content-box',
};

const fullWidthInnerInlineStyle: React.CSSProperties = {
	...innerInlineStyle,
	paddingInlineStart: token('space.400'),
	paddingInlineEnd: token('space.400'),
	paddingBlockStart: token('space.400'),
	maxWidth: 'none',
};

export type AdminPageLayoutProps = {
	children: ReactNode;
	/**
	 * Optional slot rendered above `children` and inside the centred column.
	 * Intended for upsell / change-edition banners that should sit within the
	 * page gutter, not at the screen edge.
	 */
	banner?: ReactNode;
	fullWidth?: boolean;
};

export const AdminPageLayout: FC<AdminPageLayoutProps> = ({ children, banner, fullWidth }) => (
	<Box xcss={styles.outer} as="main">
		<Box
			xcss={styles.inner}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- intentional inline style to match Mission Control
			style={fullWidth ? fullWidthInnerInlineStyle : innerInlineStyle}
			testId="admin-page-layout-inner"
		>
			{banner}
			{children}
		</Box>
	</Box>
);
